import React from "react";
import { Guid } from "guid-typescript";

import ButtonThemes, { ButtonTheme } from "../../types/enums/ButtonThemes";
import IconStyles, { IconStyle } from "../../types/enums/IconStyles";
import { Alignment } from "../../types/system/Layout";

import "./ButtonMenu.scss";
import { ButtonProps } from "./Button";
import { createPortal } from "react-dom";

interface ButtonMenuProps {
    text?: string;
    bold?: boolean;
    icon?: string;
    iconStyle?: IconStyle;
    disabled?: boolean;
    theme?: ButtonTheme;
    fullWidth?: boolean;
    align?: Alignment;
    tooltip?: string;
    tooltipPlace?: string;

    children?:
        | React.ReactElement<ButtonProps>
        | React.ReactElement<ButtonProps>[];
}

interface ButtonMenuState {
    ref: React.RefObject<HTMLButtonElement>;
    anchor: string;
    isTop?: boolean;
    isRight?: boolean;
    open: boolean;
    focused: boolean;
}

export const ButtonMenuContext = React.createContext({
    onFocus: () => {},
    onBlur: () => {},
    onClick: () => {},
});

class ButtonMenu extends React.Component<ButtonMenuProps, ButtonMenuState> {
    state: ButtonMenuState = {
        ref: React.createRef<HTMLButtonElement>(),
        anchor: `--${Guid.create().toString()}`,
        open: false,
        focused: false,
    };

    componentDidMount(): void {
        const page = document.querySelector(".page-body");
        const buttonRect = this.state.ref.current?.getBoundingClientRect();

        const isTop = (buttonRect?.top ?? 0) > (page?.clientHeight ?? 0) / 2;
        const isRight = (buttonRect?.left ?? 0) > (page?.clientWidth ?? 0) / 2;

        this.setState({
            isTop,
            isRight,
        });
    }

    open() {
        this.setState({ open: true });
    }

    close() {
        this.setState({ open: false });
    }

    toggle() {
        if (this.props.disabled) return;
        if (this.state.open) this.close();
        else this.open();
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.close();
            }
        }, 100);
    }

    handleClick(event: React.MouseEvent<HTMLButtonElement>) {
        if (this.props.disabled) return;
        this.toggle();
    }

    render(): React.ReactNode {
        let classTheme = "normal-button";

        switch (this.props.theme) {
            case ButtonThemes.OK:
                classTheme = "ok-button";
                break;
            case ButtonThemes.Theme:
                classTheme = "theme-button";
                break;
            case ButtonThemes.Minimal:
                classTheme = "minimal-button";
                break;
            case ButtonThemes.None:
                classTheme = "none-button";
                break;
        }

        switch (this.props.align) {
            case Alignment.Left:
                classTheme += " left";
                break;
            case Alignment.Center:
                classTheme += " center";
                break;
            case Alignment.Right:
                classTheme += " right";
                break;
        }

        if (this.props.fullWidth) classTheme += " full";

        if (this.props.bold) classTheme += " bold";

        let iconStyle = this.props.iconStyle ?? IconStyles.Normal;

        let classes = "button-menu";

        if (this.state.isTop) classes += " pos-top";
        else classes += " pos-bottom";

        if (this.state.isRight) classes += " pos-right";
        else classes += " pos-left";

        const contextValue = {
            onFocus: this.handleFocus.bind(this),
            onBlur: this.handleBlur.bind(this),
            onClick: this.handleClick.bind(this),
        };

        return (
            <ButtonMenuContext.Provider value={contextValue}>
                <div className="button-menu-container">
                    <button
                        ref={this.state.ref}
                        className={`toolbar-button ${classTheme}`}
                        style={{ anchorName: this.state.anchor }}
                        disabled={this.props.disabled ?? false}
                        data-tip={this.props.tooltip}
                        data-place={this.props.tooltipPlace}
                        onFocus={this.handleFocus.bind(this)}
                        onBlur={this.handleBlur.bind(this)}
                        onClick={this.handleClick.bind(this)}
                        tabIndex={0}>
                        {this.props.icon && (
                            <i className={iconStyle}>{this.props.icon}</i>
                        )}
                        {(this.props.text || !this.props.icon) && (
                            <span>{this.props.text ?? "Button"}</span>
                        )}
                    </button>
                    {this.state.open &&
                        createPortal(
                            <div
                                className={classes}
                                style={{
                                    positionAnchor: this.state.anchor,
                                }}>
                                {this.props.children}
                            </div>,
                            document.querySelector(".page-elements"),
                        )}
                </div>
            </ButtonMenuContext.Provider>
        );
    }
}

export default ButtonMenu;
